import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';

interface User {
  id: string;
  email: string;
  name: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  loading: boolean;
  refreshToken: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Initialize auth state from localStorage
  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('token');
      const refreshToken = localStorage.getItem('refreshToken');
      const userData = localStorage.getItem('user');

      if (token && refreshToken && userData) {
        try {
          // Set axios default header
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          
          // Parse user data
          const parsedUser = JSON.parse(userData);
          setUser(parsedUser);

          // Verify token is still valid by making a test request
          await axios.get(API_ENDPOINTS.files.list);
        } catch (error) {
          // Token is invalid, try to refresh
          console.log('Token expired, attempting refresh...');
          const refreshSuccess = await refreshTokenFromStorage();
          if (!refreshSuccess) {
            // Refresh failed, clear auth state
            clearAuthState();
          }
        }
      }
      setLoading(false);
    };

    initializeAuth();
  }, []);

  const refreshTokenFromStorage = async (): Promise<boolean> => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) return false;

    try {
      const response = await axios.post<{ user: User; token: string; refreshToken: string }>(API_ENDPOINTS.auth.refresh, {
        refreshToken,
      });
      
      const { user: userData, token, refreshToken: newRefreshToken } = response.data;
      setUser(userData);
      localStorage.setItem('token', token);
      localStorage.setItem('refreshToken', newRefreshToken);
      localStorage.setItem('user', JSON.stringify(userData));
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  };

  const clearAuthState = () => {
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
  };

  const login = async (email: string, password: string): Promise<boolean> => {
    setLoading(true);
    try {
      const response = await axios.post<{ user: User; token: string; refreshToken: string }>(API_ENDPOINTS.auth.login, {
        email,
        password,
      });
      
      const { user: userData, token, refreshToken } = response.data;
      
      // Set storage and headers first
      localStorage.setItem('token', token);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(userData));
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Then update state - this ensures everything is ready before navigation
      setUser(userData);
      setLoading(false);
      
      return true;
    } catch (error) {
      console.error('Login failed:', error);
      setLoading(false);
      return false;
    }
  };

  const logout = () => {
    clearAuthState();
  };

  const refreshToken = async (): Promise<boolean> => {
    return await refreshTokenFromStorage();
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    login,
    logout,
    loading,
    refreshToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 